import RefundSummary from '@/components/manage/refund/summary/RefundSummary';
import PriceDisplay from '@/components/PriceDisplay';
import {
  manageFlowBookingSelector,
  refundDetailsSelector,
  refundSelectedLegAdmissionsSelector,
} from '@/features/manage/manageSelectors';
import { useBookingRefundEligibleTrips } from '@/hooks/useBookingRefundEligibleTrips';
import { TransText } from '@/i18n/trans/text';
import { useSelector } from '@/store/utils';
import type {
  AdmissionDTO,
  BookedTripDTO,
  LegDTO,
  PassengerDTO,
} from '@/types/dto/agent';
import { formatDateWithDayOfWeek } from '@/utils/dateTime';
import { Icons, Typography } from '@fleet/webshop-ui';
import { type FC, type ReactNode, useMemo } from 'react';

export const RefundLegOverview: FC = () => {
  const booking = useSelector(manageFlowBookingSelector);
  const { refundEligibleOutboundTrips, refundEligibleInboundTrips } =
    useBookingRefundEligibleTrips(booking);
  const selectedLegAdmissions = useSelector(
    refundSelectedLegAdmissionsSelector
  );
  const refundSummary = useSelector(refundDetailsSelector);

  const passengerMap = useMemo(
    () =>
      new Map(
        booking?.passengers?.map((passenger) => [passenger.id, passenger]) ?? []
      ),
    [booking]
  );

  if (!refundSummary || !refundSummary.refundOffers) {
    return null;
  }

  return (
    <div className="p-4">
      <RefundSummary
        refundSummary={refundSummary}
        admissions={Object.values(selectedLegAdmissions).flat()}
      />
      <Typography variant="subtitle" className="my-5">
        <TransText i18nKey="youAreAboutToRefundFollowingItems" />
      </Typography>
      <div className="flex flex-col gap-3">
        {refundEligibleOutboundTrips.some((trip) =>
          trip.legs?.some(
            ({ id }) => id && selectedLegAdmissions[id]?.length > 0
          )
        ) && (
          <TripSection
            trips={refundEligibleOutboundTrips}
            title={
              <TransText
                i18nKey="outboundTrips"
                values={{ count: refundEligibleOutboundTrips.length }}
              />
            }
            selectedLegAdmissions={selectedLegAdmissions}
            passengerMap={passengerMap}
          />
        )}
        {refundEligibleInboundTrips.some((trip) =>
          trip.legs?.some(
            ({ id }) => id && selectedLegAdmissions[id]?.length > 0
          )
        ) && (
          <TripSection
            trips={refundEligibleInboundTrips}
            title={
              <TransText
                i18nKey="returnTrips"
                values={{ count: refundEligibleInboundTrips.length }}
              />
            }
            selectedLegAdmissions={selectedLegAdmissions}
            passengerMap={passengerMap}
          />
        )}
      </div>
    </div>
  );
};

const AdmissionDetails: FC<{
  admission: AdmissionDTO;
  passengerMap: Map<string | undefined, PassengerDTO>;
}> = ({ admission, passengerMap }) => {
  const firstPassengerId = admission.passengerIds?.[0];
  const passenger = passengerMap.get(firstPassengerId);
  const passengerName = `${passenger?.firstName} ${passenger?.lastName}`;

  return (
    <div className="mt-3 flex flex-col gap-3">
      <AdmissionItem
        icon={<Icons.ticket height={16} width={16} className="text-primary" />}
        title={admission.productSummary || admission.summary}
        subtitle={passengerName}
        amount={admission.price?.amount}
      />
      {admission.ancillaries?.map((ancillary, index) => (
        <AdmissionItem
          key={index}
          icon={
            <Icons.bicycle height={16} width={16} className="text-primary" />
          }
          title={ancillary.productSummary}
          subtitle={passengerName}
          amount={ancillary.price?.amount}
        />
      ))}
    </div>
  );
};

const AdmissionItem: FC<{
  icon: ReactNode;
  title: ReactNode;
  subtitle: string;
  amount: number | undefined;
}> = ({ icon, title, subtitle, amount }) => (
  <div className="flex flex-row items-stretch justify-between">
    <div className="flex flex-row items-start gap-2">
      <div className="flex h-5 w-5 items-center justify-center">{icon}</div>
      <div>
        <Typography variant="body1-bold">{title}</Typography>
        <Typography variant="body2" className="text-neutral">
          {subtitle}
        </Typography>
      </div>
    </div>
    <Typography variant="body2-bold" className="mt-[2px] text-primary">
      <PriceDisplay amount={amount} />
    </Typography>
  </div>
);

const TripSection: FC<{
  trips: Array<BookedTripDTO>;
  title: ReactNode;
  selectedLegAdmissions: Record<NonNullable<LegDTO['id']>, Array<AdmissionDTO>>;
  passengerMap: Map<string | undefined, PassengerDTO>;
}> = ({ trips, title, selectedLegAdmissions, passengerMap }) => (
  <div className="rounded-md border border-neutral-light">
    <div className="flex items-center justify-between rounded-t-md border-b border-neutral-light bg-primary-pale px-4 py-3">
      <div className="flex flex-row items-center gap-2">
        <Icons.transportFrom height={16} width={16} className="text-primary" />
        <Typography variant="body1-bold">{title}</Typography>
      </div>
      <Typography variant="body2" className="text-neutral">
        {formatDateWithDayOfWeek(trips[0]?.legs?.[0]?.departureTime)}
      </Typography>
    </div>
    {trips.map((trip) =>
      trip.legs?.map(({ id, originStop, destinationStop }) => {
        const legAdmissions = id ? selectedLegAdmissions[id] : null;

        if (!legAdmissions?.length) {
          return null;
        }

        return (
          <div key={id} className="px-4 py-3">
            <div className="flex flex-row items-center gap-1">
              <Typography variant="body1-bold">{originStop?.name}</Typography>
              <Icons.oneWayAlt height={16} width={16} />
              <Typography variant="body1-bold">
                {destinationStop?.name}
              </Typography>
            </div>
            {legAdmissions.map((admission) => (
              <AdmissionDetails
                key={admission.id}
                admission={admission}
                passengerMap={passengerMap}
              />
            ))}
          </div>
        );
      })
    )}
  </div>
);

export default RefundLegOverview;
